import React, { useEffect, useRef, useState } from "react";
import "./stripeTransactions.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_STRIPE_TRANSACTIONS,
  GET_CLIENT_INVOICE_BY_NUM,
} from "../../../../utils/GraphQL/queries";
import { LINK_STRIPE_TRANSACTION_TO_INVOICE } from "../../../../utils/GraphQL/mutations";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import Auth from "../../../../utils/auth";
import { hasAnyRole } from "../../../../utils/permissions";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import useNavHeightVar from "../../../../utils/useNavHeightVar";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import {
  reasonLine,
  mismatchWarning,
  wrongInvoiceLine,
  linkResultMessage,
  listOutcome,
  lookupOutcome,
  createLatestGate,
  withoutRow,
} from "../../../../utils/stripeLinkText";

const StripeTransactions = () => {
  const { handleSnack } = useSnackbar();
  const userPerm = Auth.getProfile()?.data?.permission || "";
  const canLink = hasAnyRole(userPerm, ["admin", "office"]);

  const [trans, setTrans] = useState([]);
  // The list is a worklist (registry §7.2): "Nothing waiting" must only show
  // once a fetch has actually SUCCEEDED — not while loading, not on a failure.
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTxn, setDialogTxn] = useState(null);
  const [invoiceNumInput, setInvoiceNumInput] = useState("");
  const [lookedUpInvoice, setLookedUpInvoice] = useState(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Latest-request-wins for the list (a refetch after a link must not be
  // overwritten by an older response) and for the invoice lookup (an
  // auto-lookup still in flight must not replace what the office typed since).
  const listGate = useRef(createLatestGate()).current;
  const lookupGate = useRef(createLatestGate()).current;
  // Where focus goes after a linked row leaves the list (the modal's
  // return-focus target was that row's button, which no longer exists).
  const titleRef = useRef(null);

  // network-only: Apollo's default (cache-first) showed a cached list on a
  // revisit — a payment linked minutes ago would still be on it. No request
  // dedup: a refetch issued while an identical request is in flight must get
  // its OWN response, or the latest-wins gate would accept the older list.
  const [getStripeTransactionsCall] = useLazyQuery(GET_STRIPE_TRANSACTIONS, {
    fetchPolicy: "network-only",
    context: { queryDeduplication: false },
  });
  const [getInvoiceByNum] = useLazyQuery(GET_CLIENT_INVOICE_BY_NUM, {
    fetchPolicy: "network-only",
  });
  const [linkTransactionCall] = useMutation(LINK_STRIPE_TRANSACTION_TO_INVOICE);

  useEffect(() => {
    queryTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The page's viewport lock is CSS now (`height: calc(100svh - var(--bdr-nav-h))`
   * in stripeTransactions.css); this hook publishes the variable it reads.
   *
   * What it replaced: a `window.innerHeight - navHeight` read behind an 80ms
   * `setTimeout`, with the result held in state and applied as an inline
   * `style`. The timeout was there because the nav is rendered behind a gate
   * and is not always measurable on the first frame — `useNavHeightVar` solves
   * that properly with a ResizeObserver and a retry window, and it was already
   * doing it for three other pages.
   */
  useNavHeightVar();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const queryTransactions = async () => {
    const token = listGate.begin();
    let result;
    try {
      result = await getStripeTransactionsCall();
    } catch (err) {
      result = { error: err };
    }
    if (!listGate.isCurrent(token)) return; // a newer fetch owns the list
    // Apollo resolves (never rejects) a failed lazy query: check `error`.
    const outcome = listOutcome(result);
    setFailed(outcome.failed);
    if (!outcome.failed) setTrans(outcome.rows);
    setLoaded(true);
  };

  // Take the row off the screen now (it can't be clicked twice), then let the
  // server say what is still waiting.
  const dropAndRefresh = (id) => {
    setTrans((prev) => withoutRow(prev, id));
    queryTransactions();
    requestAnimationFrame(() => titleRef.current?.focus?.());
  };

  const transactionTime = (mill) => {
    const timeStr = BAGroupTimeFormat(mill, true, true);

    const today = /today/gi.test(timeStr);
    const oneYear = 1000 * 60 * 60 * 24 * 365;
    const olderThenAYear = Date.now() - oneYear > parseInt(mill);

    const time = today
      ? `${timeStr?.split("@")[1]}`
      : olderThenAYear
      ? `${timeStr?.split(",")[1]?.replace(/@/gi, "-")}`
      : `${BAGroupTimeFormat(mill, true, false)?.replace(/@/gi, "-")}`;

    return time;
  };

  const openLinkDialog = (txn) => {
    // Pre-fill the invoice the payment was taken for, and look it up straight
    // away — the mismatch case is "fix the invoice, then link" on THAT invoice.
    const prefill = txn?.invoiceNum ? String(txn.invoiceNum) : "";
    setDialogTxn(txn);
    setInvoiceNumInput(prefill);
    setLookedUpInvoice(null);
    setLookupDone(false);
    setSubmitting(false);
    setDialogOpen(true);
    if (prefill) handleLookup(prefill);
  };

  const closeLinkDialog = () => {
    lookupGate.begin(); // drop any lookup still in flight
    setDialogOpen(false);
    setDialogTxn(null);
  };

  const handleLookup = async (numArg) => {
    const num = (typeof numArg === "string" ? numArg : invoiceNumInput).trim();
    if (!num) {
      handleSnack("Enter an invoice number", 2000);
      return;
    }
    const token = lookupGate.begin();
    let result;
    try {
      result = await getInvoiceByNum({ variables: { invoiceNum: num } });
    } catch (err) {
      result = { error: err };
    }
    if (!lookupGate.isCurrent(token)) return;
    const outcome = lookupOutcome(result);
    if (outcome.failed) {
      handleSnack("Error looking up invoice", 3000);
      return;
    }
    setLookedUpInvoice(outcome.invoice);
    setLookupDone(true);
    if (!outcome.invoice) {
      handleSnack(`No invoice found for #${num}`, 3000);
    }
  };

  const handleConfirmLink = async () => {
    if (!dialogTxn || !lookedUpInvoice) return;
    const txnId = dialogTxn.id;
    setSubmitting(true);
    try {
      const { data } = await linkTransactionCall({
        variables: {
          stripeTransactionId: txnId,
          // Link exactly the invoice on screen, not whatever is in the field.
          invoiceNum: String(lookedUpInvoice.invoiceNum),
        },
      });
      const result = data?.linkStripeTransactionToInvoice;
      if (result?.alreadyPaid) {
        handleSnack(
          `Invoice #${result.invoiceNum} is already paid — no changes made.`,
          5000
        );
        closeLinkDialog();
        dropAndRefresh(txnId);
        return;
      }
      if (result?.success) {
        handleSnack(linkResultMessage(result), 6000);
        closeLinkDialog();
        dropAndRefresh(txnId);
      } else {
        handleSnack("Link failed.", 4000);
        setSubmitting(false);
      }
    } catch (err) {
      // A refusal ("already booked …") or a failure: say it, then let the
      // server's list decide whether the row is still waiting.
      handleSnack(err.message || "Error linking transaction", 6000);
      closeLinkDialog();
      queryTransactions();
    }
  };

  const dialogWrongInvoice = wrongInvoiceLine(dialogTxn, lookedUpInvoice);
  const dialogMismatch = mismatchWarning(dialogTxn, lookedUpInvoice);

  return (
    <div className="trans-page">
      <div className="trans-page-container bdr-section-panel">
        {/* The blue band. `.bdr-section-panel-hdr` does not scroll, so the
            header no longer needs `position: sticky` to stay put. */}
        <div className="bdr-section-panel-hdr">
          <h2 className="bdr-section-panel-title" ref={titleRef} tabIndex={-1}>
            Recent Transactions
          </h2>
        </div>

        {/* The gray body the transaction cards sit on — and the only part that
            scrolls, which is what keeps the page itself still. */}
        <div className="bdr-section-panel-body bdr-section-panel-body--scroll trans-page-body">
          {failed && (
            <p className="bdr-alert bdr-alert--danger" role="alert">
              Couldn't load transactions.
            </p>
          )}
          {!loaded && !failed && <div className="bdr-loading">Loading…</div>}
          {loaded && !failed && trans?.length === 0 && (
            /* This list is a WORKLIST (registry §7.2): empty is the GOOD state,
               it means nothing is waiting on a human. Saying so beats an empty
               panel that reads as a page that failed to load. */
            <p className="bdr-empty">
              Nothing waiting to be linked.
            </p>
          )}
          {trans?.length > 0 &&
            trans?.map((item) => {
              const date = transactionTime(item?.dateCreated);
              const tip = parseInt(item?.tip) || 0;
              const why = reasonLine(item);

              return (
                <div key={`transaction-${item?.id}`} className="trans-item">
                  <div className="trans-item-amount">
                    Total: {priceStrFormatter(item?.amount)}{" "}
                    <span className="trans-item-tip">· {date}</span>
                  </div>
                  {tip !== 0 && (
                    <div className="trans-item-tip">
                      + {priceStrFormatter(tip?.toString())} Tip
                    </div>
                  )}
                  {why && <div className="trans-item-tip">{why}</div>}
                  {canLink && (
                    <BdrButton
                      size="sm"
                      variant="action"
                      className="trans-link-btn"
                      onClick={() => openLinkDialog(item)}
                    >
                      Link to Invoice
                    </BdrButton>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Link to Invoice Dialog */}
      <BdrModal
        open={dialogOpen}
        onClose={closeLinkDialog}
        size="sm"
        variant="panel"
        title="Link Transaction to Invoice"
        /* Not dismissable while a link is in flight: its completion closes the
           dialog, and would otherwise close whichever one was opened since. */
        closeOnEsc={!submitting}
        closeOnBackdrop={!submitting}
        showClose={!submitting}
        actions={
          /* Only once there is something to link — the action row stays out of
             the way while the field is still empty. A mismatch WARNS but does
             not block (owner decisions D16, D21). */
          lookupDone && lookedUpInvoice ? (
            <BdrButton
              variant="confirm-solid"
              onClick={() => handleConfirmLink()}
              disabled={submitting}
            >
              {submitting ? "Linking..." : "Confirm Link & Send Receipt"}
            </BdrButton>
          ) : null
        }
      >
        {dialogTxn && (
          <div className="trans-link-dialog-txn">
            <span>Amount: {priceStrFormatter(dialogTxn.amount)}</span>
            {parseInt(dialogTxn.tip) > 0 && (
              <span> + {priceStrFormatter(dialogTxn.tip)} Tip</span>
            )}
            <span className="trans-item-tip">
              {" "}— {transactionTime(dialogTxn.dateCreated)}
            </span>
          </div>
        )}

        <div className="trans-link-dialog-row">
          <BdrInput
            className="trans-link-field"
            placeholder="Invoice Number"
            value={invoiceNumInput}
            onChange={(e) => {
              lookupGate.begin(); // a lookup in flight no longer matches the field
              setInvoiceNumInput(e.target.value);
              setLookedUpInvoice(null);
              setLookupDone(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            disabled={submitting}
            inputMode="numeric"
          />
          <BdrButton onClick={() => handleLookup()} disabled={submitting}>
            Look Up
          </BdrButton>
        </div>

        {lookupDone && lookedUpInvoice && (
          <div className="trans-link-preview">
            <div><strong>Invoice #{lookedUpInvoice.invoiceNum}</strong></div>
            <div>Customer: {lookedUpInvoice.customerName}</div>
            <div>Total: {priceStrFormatter(lookedUpInvoice.invoiceTotal)}</div>
            {lookedUpInvoice.invoicePaid && (
              <p className="bdr-alert bdr-alert--warning bdr-alert--start">
                Already marked paid ({lookedUpInvoice.paidBy})
              </p>
            )}
            {dialogWrongInvoice && (
              <p className="bdr-alert bdr-alert--warning bdr-alert--start">
                {dialogWrongInvoice}
              </p>
            )}
            {dialogMismatch && (
              <p className="bdr-alert bdr-alert--warning bdr-alert--start" role="alert">
                {dialogMismatch}
              </p>
            )}
          </div>
        )}
      </BdrModal>
    </div>
  );
};
export default StripeTransactions;
